import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';
import { GET_SCHEMAS_LIST } from '../../graphql/queries/schemas.queries';
import {
  SchemasListResponse,
  FindSchemaListInput,
} from '../../types/schemas.types';

interface CreateEditTableModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: TableFormData) => void;
  mode: 'create' | 'edit';
  saving?: boolean;
  saveError?: string | null;
}

export interface TableFormData {
  id?: string;
  schemaId: string;
  schemaName: string;
  tableName: string;
  description: string;
  purpose: string;
  primaryKeyColumns: string[];
  keywords: string[];
  businessContext: string;
}

const CreateEditTableModal: React.FC<CreateEditTableModalProps> = ({
  open,
  onClose,
  onSave,
  mode,
  saving = false,
  saveError = null,
}) => {
  const { selectedTable, loading } = useAppSelector((state) => state.tables);

  const [schemaId, setSchemaId] = useState('');
  const [tableName, setTableName] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState('');
  const [primaryKeyColumns, setPrimaryKeyColumns] = useState('');
  const [keywords, setKeywords] = useState('');
  const [businessContext, setBusinessContext] = useState('');
  const [schemas, setSchemas] = useState<any[]>([]);

  const [fetchSchemas, { loading: loadingSchemas }] = useLazyQuery<
    { schemas: SchemasListResponse },
    { params: FindSchemaListInput }
  >(GET_SCHEMAS_LIST, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (open) {
      loadSchemas();
    }
  }, [open]);

  useEffect(() => {
    if (mode === 'edit' && selectedTable) {
      setSchemaId(selectedTable.schemaId);
      setTableName(selectedTable.tableName);
      setDescription(selectedTable.description);
      setPurpose(selectedTable.purpose);
      setPrimaryKeyColumns(selectedTable.primaryKeyColumns.join(', '));
      setKeywords(selectedTable.keywords.join(', '));
      setBusinessContext(selectedTable.businessContext);
    } else if (mode === 'create') {
      setSchemaId('');
      setTableName('');
      setDescription('');
      setPurpose('');
      setPrimaryKeyColumns('');
      setKeywords('');
      setBusinessContext('');
    }
  }, [mode, selectedTable, open]);

  const loadSchemas = async () => {
    try {
      const { data } = await fetchSchemas({
        variables: {
          params: {
            search: null,
            page: 1,
            pageSize: 100,
          },
        },
      });

      if (data?.schemas?.success && data.schemas.data) {
        setSchemas(data.schemas.data.schemas);
      }
    } catch (err: any) {
      console.error('Error loading schemas:', err);
    }
  };

  const handleSave = () => {
    const selectedSchema = schemas.find((s) => s.id === schemaId);
    const schemaName = selectedSchema?.schemaName || '';

    const formData: TableFormData = {
      schemaId,
      schemaName,
      tableName,
      description,
      purpose,
      primaryKeyColumns: primaryKeyColumns
        .split(',')
        .map((col) => col.trim())
        .filter((col) => col.length > 0),
      keywords: keywords
        .split(',')
        .map((kw) => kw.trim())
        .filter((kw) => kw.length > 0),
      businessContext,
    };

    if (mode === 'edit' && selectedTable) {
      formData.id = selectedTable.id;
    }

    onSave(formData);
  };

  const handleClose = () => {
    onClose();
  };

  const isFormValid =
    schemaId && tableName && description && purpose && primaryKeyColumns && businessContext;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Crear Nueva Tabla' : 'Editar Tabla'}
      </DialogTitle>
      <DialogContent>
        {loading || loadingSchemas ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <FormControl fullWidth margin="normal" required disabled={saving}>
              <InputLabel>Esquema</InputLabel>
              <Select
                value={schemaId}
                onChange={(e) => setSchemaId(e.target.value)}
                label="Esquema"
              >
                {schemas.map((schema) => (
                  <MenuItem key={schema.id} value={schema.id}>
                    {schema.schemaName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Nombre de la Tabla"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              margin="normal"
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={2}
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Propósito"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Columnas de Llave Primaria (separadas por coma)"
              value={primaryKeyColumns}
              onChange={(e) => setPrimaryKeyColumns(e.target.value)}
              margin="normal"
              required
              disabled={saving}
              helperText="Ejemplo: id, tenant_id"
            />

            <TextField
              fullWidth
              label="Palabras Clave (separadas por coma)"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              margin="normal"
              disabled={saving}
              helperText="Ejemplo: clientes, prospectos, leads"
            />

            <TextField
              fullWidth
              label="Contexto de Negocio"
              value={businessContext}
              onChange={(e) => setBusinessContext(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              required
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || loadingSchemas || saving || !isFormValid}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditTableModal;
